export const PAPER_PRESETS = {
  label: { label: 'Etichetta / rotolo', width: null, height: null },
  A4: { label: 'A4 · 210×297 mm', width: 210, height: 297 },
  A5: { label: 'A5 · 148×210 mm', width: 148, height: 210 },
  '100x150': { label: '100×150 mm', width: 100, height: 150 },
  '100x100': { label: '100×100 mm', width: 100, height: 100 },
  '70x50': { label: '70×50 mm', width: 70, height: 50 },
  '60x40': { label: '60×40 mm', width: 60, height: 40 },
  '50x30': { label: '50×30 mm', width: 50, height: 30 },
  '40x50': { label: '40×50 mm', width: 40, height: 50 },
  custom: { label: 'Personalizzato', width: null, height: null },
};

const number = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export function getPrintGeometry({ layout, paper, orientation, margin, gap, customW, customH }) {
  const labelW = Math.max(1, number(layout?.width_mm, 60));
  const labelH = Math.max(1, number(layout?.height_mm, 40));
  let base;
  if (paper === 'label') base = { width: labelW, height: labelH };
  else if (paper === 'custom') base = { width: Math.max(20, number(customW, 210)), height: Math.max(20, number(customH, 297)) };
  else base = PAPER_PRESETS[paper] || PAPER_PRESETS.A4;

  const effectiveOrientation = paper === 'label' ? 'portrait' : orientation;
  const finalPaper = effectiveOrientation === 'landscape'
    ? { width: base.height, height: base.width }
    : { width: base.width, height: base.height };
  const effectiveMargin = paper === 'label' ? 0 : Math.max(0, number(margin, 0));
  const effectiveGap = paper === 'label' ? 0 : Math.max(0, number(gap, 0));
  const printableW = Math.max(0, finalPaper.width - 2 * effectiveMargin);
  const printableH = Math.max(0, finalPaper.height - 2 * effectiveMargin);
  const fits = printableW >= labelW && printableH >= labelH;
  const cols = fits ? Math.max(1, Math.floor((printableW + effectiveGap + 1e-9) / (labelW + effectiveGap))) : 1;
  const rows = fits ? Math.max(1, Math.floor((printableH + effectiveGap + 1e-9) / (labelH + effectiveGap))) : 1;

  return {
    labelW, labelH, finalPaper, effectiveOrientation, effectiveMargin, effectiveGap,
    printableW, printableH, fits, cols, rows, labelsPerPage: cols * rows,
  };
}

export function buildPrintJob({ lotId, templateId, paper, copies, geometry, printerId = null }) {
  return {
    lot_id: lotId,
    template_id: templateId,
    printer_id: printerId || undefined,
    paper,
    paper_width_mm: geometry.finalPaper.width,
    paper_height_mm: geometry.finalPaper.height,
    orientation: geometry.effectiveOrientation,
    margin_mm: geometry.effectiveMargin,
    gap_mm: geometry.effectiveGap,
    copies: Math.max(1, Math.min(500, Math.floor(number(copies, 1)))),
  };
}

async function parseError(response, fallback) {
  const body = await response.json().catch(() => ({}));
  throw new Error(body.detail || fallback);
}

export async function createPrintPdf(backendUrl, job) {
  const response = await fetch(`${backendUrl}/api/labels/from-template`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(job),
  });
  if (!response.ok) return parseError(response, 'Errore durante la generazione del PDF');
  return response.json();
}

export async function sendDirectPrint(backendUrl, job) {
  const response = await fetch(`${backendUrl}/api/labels/direct-print`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(job),
  });
  if (!response.ok) return parseError(response, 'Errore durante la stampa diretta');
  return response.json();
}

export async function confirmClientPrint(backendUrl, jobId, status, error = '') {
  const response = await fetch(`${backendUrl}/api/labels/direct-print/${encodeURIComponent(jobId)}/client-result`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ status, error }),
  });
  if (!response.ok) return parseError(response, 'Impossibile confermare l’esito della stampa');
  return response.json();
}

export async function fetchCompatiblePrinters(backendUrl, widthMm, heightMm) {
  const query = new URLSearchParams({ width_mm: String(widthMm), height_mm: String(heightMm) });
  const response = await fetch(`${backendUrl}/api/printers/compatible?${query.toString()}`, { credentials: 'include' });
  if (!response.ok) return parseError(response, 'Impossibile verificare le stampanti');
  return response.json();
}

export async function downloadPdf(backendUrl, labelId, filename = 'etichetta.pdf') {
  const response = await fetch(`${backendUrl}/api/labels/${labelId}/view`, { credentials: 'include' });
  if (!response.ok) throw new Error('PDF non disponibile');
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
